import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  useToggleScrapbookReactionMutation,
  useDeleteScrapbookPostMutation,
} from "../../../store/slices/api";
import { styleFor } from "./styles/registry";
import MarginNote from "./MarginNote";
import EditPost from "./EditPost";
import Comments from "./Comments";
import { categoryLabel } from "./categories";
import { formatDate, shortName } from "./util";

// The same short set the Bulletin offers, and the same set the server accepts.
// Deliberately fixed: a free-form emoji would render on every member's screen.
// (Sam, 2026-09-16: a wider set is worth looking at later — either a bigger
// basin here or something members can add to. Noted, not built.)
const EMOJI = ["❤️", "😂", "😮", "👏", "🙏"];

/**
 * The way to add a reaction — a labelled pill, always on screen.
 *
 * It used to be a bare ＋ that only appeared once the mouse was over the card.
 * Sam, 2026-09-16: "the reactions tab needs to be much more clearly there and
 * advertised it is there." Nobody hovers on a phone, and nobody guesses what a
 * lone plus sign does, so it now says what it is and never hides.
 *
 * The accessible name CONTAINS the visible word "React", so voice control
 * ("click React") reaches it — WCAG 2.5.3 Label in Name — but does not lead with
 * it, because the five emoji buttons inside are named "React ❤️" and so on.
 * Picking an emoji hands focus back to this button rather than dropping the
 * keyboard user at the top of the page.
 */
function AddReaction({ onPick }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const opener = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div className="relative" ref={box}>
      <button
        ref={opener}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Add a reaction — React"
        className={"inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-full " +
                   "border border-dashed border-dupontGoldLt/70 bg-transparent " +
                   "font-blairBody text-[12px] font-semibold tracking-[0.04em] " +
                   "text-dupontGoldDk hover:border-dupontGold hover:bg-dupontCream/70 " +
                   "transition-colors motion-reduce:transition-none " +
                   (open ? "border-dupontGold bg-dupontCream/70" : "")}
      >
        <span aria-hidden="true" className="text-[13px] leading-none">＋</span>
        React
      </button>

      {open && (
        <div role="group" aria-label="Choose a reaction"
             className="absolute z-10 bottom-full left-0 mb-[6px] flex gap-[3px] p-[5px]
                        bg-white dark:bg-neutral-800 border border-dupontGoldLt/40
                        rounded-[13px] shadow-lg">
          {EMOJI.map((e) => (
            <button key={e} type="button"
                    onClick={() => { onPick(e); setOpen(false); opener.current?.focus(); }}
                    aria-label={`React ${e}`}
                    className="px-[7px] py-[3px] rounded-full text-[15px] hover:bg-dupontCream dark:hover:bg-neutral-700">
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PostCard({ post, viewerId, isAdmin, index = 0 }) {
  const [toggleReaction] = useToggleScrapbookReactionMutation();
  const [deletePost] = useDeleteScrapbookPostMutation();

  // Editing is a state of THIS card, not a page mode. Opening one post's editor
  // must not disturb anybody else's post further down the wall.
  const [editing, setEditing] = React.useState(false);
  // "remove" asks first, right where the button was (Sam, 2026-09-27).
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  const [removeError, setRemoveError] = React.useState("");
  const [removing, setRemoving] = React.useState(false);

  const mine = post.my_reactions || [];
  const tally = post.reactions || {};
  // The same condition governs both controls. The server checks it again against
  // the token; this only decides what is worth drawing.
  const canDelete = post.author_individual_id === viewerId || isAdmin;
  const canEdit = canDelete;


  return (
    // The article is pure LAYOUT: a margin column and the card. Where/When/What
    // else sits OUTSIDE the white box, on the cream, and the side alternates
    // down the page so the eye keeps crossing the centre column.
    <article
      className={
        // THREE columns, always: margin · card · margin. The card lives in the
        // middle one and never moves, so the column of photographs stays put
        // down the whole page. Only the note changes side.
        //
        // An earlier version used two columns and flipped their order, which
        // swung every card 168px left and right as you scrolled. That is what
        // made it look broken.
        //
        // Four rows: byline, caption, photograph, everything after. The card
        // spans all four and mirrors them with subgrid, which lets the note —
        // which sits OUTSIDE the card — be placed into the photograph's row and
        // line up with it exactly.
        "group grid mb-[24px] gap-x-[22px] " +
        "grid-rows-[auto_auto_auto_auto] " +
        "grid-cols-[168px_minmax(0,1fr)_168px] " +
        "max-[820px]:!grid-cols-[minmax(0,1fr)] max-[820px]:!grid-rows-none max-[820px]:gap-[8px] " +
        (index % 2 === 1 ? "flip" : "")
      }
    >
      {/* Beside the photographs, which are row 2 now that the words sit below. */}
      <MarginNote place={post.place} whenText={post.when_text} extraNote={post.extra_note} />

      <div
        className="col-start-2 row-start-1 row-end-5 grid grid-rows-subgrid
                   max-[820px]:!col-auto max-[820px]:!row-auto max-[820px]:!block
                   min-w-0 bg-white dark:bg-neutral-900 rounded-[14px]
                   border border-dupontGoldLt/30 shadow-sm px-[17px] pt-[15px] pb-[13px]
                   transition-[transform,box-shadow] duration-200 ease-out
                   group-hover:-translate-y-[3px] group-hover:shadow-xl
                   motion-reduce:transition-none motion-reduce:group-hover:translate-y-0
                   [@media(hover:none)]:group-hover:translate-y-0
                   "
      >
      <header className="row-start-1 self-start flex flex-wrap items-baseline gap-x-[10px] gap-y-[4px] pb-[8px] max-[820px]:!row-auto">
        {/* The poster's name opens their page in the family directory (Sam, 2026-09-15). */}
        <Link
          to={`/connect?tab=directory&person=${post.author_individual_id}`}
          className="font-display font-semibold text-[16px] leading-[1.2] text-dupontBlue dark:text-darkText
                     no-underline hover:underline underline-offset-[3px] decoration-dupontGold/70"
        >
          {shortName(post.author)}
        </Link>
        <span className="font-blairBody text-[12px] text-gray-400 whitespace-nowrap">
          {formatDate(post.created_at)}
        </span>
        <span className="ml-auto font-blairBody font-medium text-[12px] tracking-[0.09em]
                         uppercase text-dupontGold/70">
          {categoryLabel(post.category)}
        </span>
        {/* Both controls are always visible. They used to appear only on hover,
            which meant most members on a laptop never discovered that their own
            post was theirs to change. A control nobody can find is not a control. */}
        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Edit this post"
            className="text-[12px] text-gray-400 hover:text-dupontBlue dark:hover:text-dupontGoldLt
                       focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                       focus-visible:outline-dupontBlue rounded-[2px]"
          >
            edit
          </button>
        )}
        {canDelete && !confirmingRemove && (
          <button
            type="button"
            onClick={() => {
              // Asked inline, not with window.confirm, which blocks the whole tab.
              setRemoveError("");
              setConfirmingRemove(true);
            }}
            aria-label="Remove this post"
            className="text-[12px] text-gray-400 hover:text-red-600
                       focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                       focus-visible:outline-red-600 rounded-[2px]"
          >
            remove
          </button>
        )}
        {canDelete && confirmingRemove && (
          <span role="group" aria-label="Confirm removing this post" className="text-[12px] text-gray-500 dark:text-gray-400">
            Remove this post?{" "}
            <button
              type="button"
              disabled={removing}
              onClick={async () => {
                // One tap, one delete: a double tap used to send a second one
                // and flash an error for a post that was already gone.
                if (removing) return;
                setRemoving(true);
                try {
                  await deletePost(post.id).unwrap();
                } catch {
                  setConfirmingRemove(false);
                  setRemoveError("Couldn't remove it. Please try again.");
                } finally {
                  setRemoving(false);
                }
              }}
              className="text-red-600 hover:underline font-medium
                         focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                         focus-visible:outline-red-600 rounded-[2px]"
            >
              {removing ? "Removing…" : "Yes"}
            </button>
            {" · "}
            <button
              type="button"
              // Focus lands on No, so pressing Enter twice can't delete a post.
              autoFocus
              onClick={() => setConfirmingRemove(false)}
              className="hover:underline
                         focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
                         focus-visible:outline-dupontBlue rounded-[2px]"
            >
              No
            </button>
          </span>
        )}
        {removeError && (
          <span role="alert" className="text-[12px] text-red-600">{removeError}</span>
        )}
      </header>

      <div className="row-start-2 self-start space-y-[3px] max-[820px]:!row-auto">
        {/* The post's template draws its photos; an unknown style is a Snapshot. */}
        {React.createElement(styleFor(post.style).Body, { post })}
      </div>

      {/* The poster's own words, under the pictures (Sam, 2026-09-15). Row 3 is
          always occupied, even with nothing written: if it collapsed, every row
          below would shift up on exactly the posts nobody wrote a line for. */}
      {editing ? (
        <div className="row-start-3 self-start max-[820px]:!row-auto">
          <EditPost post={post} onClose={() => setEditing(false)} />
        </div>
      ) : (
        <p className="row-start-3 self-start pt-[11px] font-body text-[18px] leading-[1.5]
                      text-gray-800 dark:text-gray-200 empty:pt-0 max-[820px]:!row-auto
                      whitespace-pre-wrap break-words [overflow-wrap:anywhere] min-w-0">
          {/* A Keepsake writes its line on the print instead. */}
          {post.style === "keepsake" ? "" : post.caption || ""}
          {/* Said quietly, but said. A thread of replies still has to make sense
              after the line it was answering changes underneath it. */}
          {post.edited && post.style !== "keepsake" && (
            <span
              className="font-blairBody text-[12px] text-gray-400 italic ml-[6px]
                         align-baseline whitespace-nowrap"
              title={
                post.edited_by_office
                  ? "Edited by the family office"
                  : "Edited by the person who posted it"
              }
            >
              {/* A single asterisk when the OFFICE tidied the words, so the person
                  who wrote them is not left wondering why their post changed. It
                  never says which admin — Sam, 2026-09-20: "not which admin
                  edited". A name here would give a member somebody to feel
                  watched by; the fact is what they are owed. */}
              · edited{post.edited_by_office ? "\u2009*" : ""}
            </span>
          )}
        </p>
      )}

      <div className="row-start-4 self-start max-[820px]:!row-auto">
      <div className="flex flex-wrap items-center gap-[6px] pt-[11px] pb-[2px]">
        {/* Only reactions that somebody has actually given. A full row of five
            greyed-out emoji is a scoreboard, and a scoreboard is the most
            watched-feeling thing you can put under a family photograph. The
            rest live behind the quiet opener at the end of the row. */}
        {EMOJI.filter((e) => (tally[e] || 0) > 0 || mine.includes(e)).map((e) => {
          const count = tally[e] || 0;
          const active = mine.includes(e);
          return (
            <button
              key={e}
              type="button"
              onClick={() => toggleReaction({ postId: post.id, emoji: e })}
              aria-pressed={active}
              aria-label={`React ${e}${count ? `, ${count} so far` : ""}`}
              className={
                "inline-flex items-center gap-[5px] px-[9px] py-[4px] rounded-full text-[13px] " +
                "border transition-colors " +
                (active
                  ? "bg-dupontGold/20 border-dupontGold/60"
                  : "bg-transparent border-dupontGoldLt/30 hover:border-dupontGold/50")
              }
            >
              <span aria-hidden="true">{e}</span>
              {count > 0 && (
                <span className="text-[13px] text-gray-600 dark:text-gray-400">{count}</span>
              )}
            </button>
          );
        })}

        <AddReaction onPick={(e) => toggleReaction({ postId: post.id, emoji: e })} />
      </div>

        <Comments post={post} viewerId={viewerId} isAdmin={isAdmin} />
      </div>
      </div>
    </article>
  );
}

export default PostCard;
